/**
 * Hook that memoizes the advanced filter input for player search queries.
 *
 * Extracts the repeated filterVariables useMemo pattern shared by the
 * player-search surfaces and the kanban board filters.
 * @module features/scouting/hooks/useFilterVariables
 */

import { useMemo } from "react";

import type { FilterState } from "@/hooks/useFilterState";

import { buildAdvancedFilterInput } from "../utils/buildFilterVariables";

/**
 * Memoizes buildAdvancedFilterInput from filter state and debounced search.
 * @param filterState - Current filter state from useFilterState
 * @param debouncedSearch - Debounced search text
 * @returns Memoized filter input suitable for the filterPlayers query
 */
const useFilterVariables = (
  filterState: FilterState,
  debouncedSearch: string
) => {
  /**
   * Resolve each selected country id to every underlying nationality entity
   * name it groups — the backend expects name strings, not UUIDs, and a
   * country groups one or more entities (its own row plus every
   * dual-nationality combination naming it), so a single country selection
   * must expand to all of them to still match dual-national players
   * (SE-8325).
   */
  const nationalityNames = useMemo(
    () =>
      filterState.selectedNationalityIds.flatMap(
        id => filterState.nationalityMemberNamesById[id] ?? [id]
      ),
    [filterState.selectedNationalityIds, filterState.nationalityMemberNamesById]
  );

  return useMemo(
    () =>
      buildAdvancedFilterInput({
        debouncedSearch,
        selectedPositionIds: filterState.selectedPositionIds,
        selectedClubIds: filterState.selectedClubIds,
        selectedWatchlistId: null,
        selectedScoutIds: filterState.selectedScoutIds,
        selectedNationalityIds: nationalityNames,
        selectedLeagueIds: filterState.selectedLeagueIds,
        selectedCompetitionIds: filterState.selectedCompetitionIds,
        selectedRoleArchetypes: filterState.selectedRoleArchetypes,
        ageRange: filterState.ageRange,
        contractMonthsRange: filterState.contractMonthsRange,
        valuationRange: filterState.valuationRange,
        gprRange: filterState.gprRange,
        fitScoreRange: filterState.fitScoreRange,
        teamStyleFitRange: filterState.teamStyleFitRange,
        carryingRange: filterState.carryingRange,
        dribblingRange: filterState.dribblingRange,
        passingRange: filterState.passingRange,
        shootingRange: filterState.shootingRange,
        gkPassingRange: filterState.gkPassingRange,
        gkShotStoppingRange: filterState.gkShotStoppingRange,
        gkPositioningRange: filterState.gkPositioningRange,
        customStatRanges: filterState.customStatRanges,
      }),
    [
      debouncedSearch,
      filterState.selectedPositionIds,
      filterState.selectedClubIds,
      filterState.selectedScoutIds,
      nationalityNames,
      filterState.selectedLeagueIds,
      filterState.selectedCompetitionIds,
      filterState.selectedRoleArchetypes,
      filterState.ageRange,
      filterState.contractMonthsRange,
      filterState.valuationRange,
      filterState.gprRange,
      filterState.fitScoreRange,
      filterState.teamStyleFitRange,
      filterState.carryingRange,
      filterState.dribblingRange,
      filterState.passingRange,
      filterState.shootingRange,
      filterState.gkPassingRange,
      filterState.gkShotStoppingRange,
      filterState.gkPositioningRange,
      filterState.customStatRanges,
    ]
  );
};

export default useFilterVariables;
